import { useCallback, useMemo } from 'react';
import { AudioElement, GroupElement, type VisualElement } from '@bynder-studio/render-core';
import { PlaybackManipulationRenderer } from '@bynder-studio/render-web';
import useEditor from '~/hooks/useEditor';
import useForceUpdate from '~/hooks/useForceUpdate';
import {
    collectTopLevelElements,
    collectTopLevelGroups,
} from '~/common/editor/timeline/timeline-actions/components/utils';

const useElementGeneralActions = (setSelectedGroups = null, selectedGroups = []) => {
    const { creativeModel, manipulationRenderer } = useEditor();
    const selectedElements = manipulationRenderer?.getSelectedElements() || [];
    const forceUpdate = useForceUpdate();
    const isActiveElements = !!selectedElements.length;
    const isActiveGroups = !!selectedGroups?.length;

    // todo: Remove after BE implements inherited locks
    const isSelected = isActiveElements && collectTopLevelElements(selectedElements).every((e) => e.locked);

    const handleSelection = useCallback(() => {
        const editorElements = creativeModel.getAllElementsRecursively();
        const selectedElements = manipulationRenderer?.getSelectedElements() || [];

        const notSelectedElements = editorElements.filter(
            (el) => !selectedElements.some((selected) => selected.id === el.id),
        );

        collectTopLevelElements(notSelectedElements).forEach((el) => {
            manipulationRenderer.selectElement(el.id, true);
        });
    }, [creativeModel, manipulationRenderer]);

    const handleLock = useCallback(() => {
        if (!isActiveElements) {
            return;
        }

        // todo: Remove after BE implements inherited locks
        const selectedElements = manipulationRenderer?.getSelectedElements() || [];
        const selectedGroups = selectedElements.filter((el) => el instanceof GroupElement);
        const locked = selectedElements.every((el) => el.locked);

        if (selectedGroups.length > 0) {
            const topLevelGroups = collectTopLevelGroups(selectedGroups);

            manipulationRenderer.selectElements(topLevelGroups.map((el) => el.id));
        }

        if (manipulationRenderer?.getSelectedElements().length > 0) {
            manipulationRenderer?.getSelectedElements().forEach((el) => {
                creativeModel.updateElement(el.id, {
                    locked: !locked,
                });
            });
        }

        forceUpdate();
    }, [isActiveElements, isSelected, selectedElements, creativeModel, forceUpdate]);

    const handleDelete = useCallback(() => {
        if (!isActiveElements) {
            return;
        }

        if (manipulationRenderer instanceof PlaybackManipulationRenderer) {
            manipulationRenderer.pausePlayback();
        }

        const editorElements = creativeModel.getAllElementsRecursively();
        const elementIdsToRemove = collectTopLevelElements(selectedElements).map((element) => element.id);
        const elementsToDelete = editorElements.filter((el) => elementIdsToRemove.includes(el.id));
        const remainingElements = editorElements.filter((el) => !elementIdsToRemove.includes(el.id));

        const elementsWithMask = editorElements.filter(
            (el) => 'mask' in el && el.mask !== null && !elementIdsToRemove.includes(el.id),
        );

        const maskedElementsToUpdate = elementsWithMask.filter((maskedElement) =>
            editorElements.find((el) => el.id === maskedElement.id),
        ) as VisualElement[];

        const detacheddMaskIds = elementsToDelete
            .filter((el) => 'mask' in el && el.mask !== null)
            .map((el: VisualElement) => el.mask.elementId);

        const masksToUpdate = remainingElements.filter((el) => detacheddMaskIds.includes(el.id));

        // Collect Single Select groups whose active child is being deleted
        // Must be done BEFORE removeElements, as removal mutates the element tree
        const singleSelectGroupUpdates: { groupId: number; newActiveChildId: number }[] = [];
        const parentGroups = new Set(
            elementsToDelete
                .map((el) => el.parent)
                .filter(
                    (parent): parent is GroupElement => parent instanceof GroupElement && parent.onlyAllowOneSelection,
                ),
        );

        parentGroups.forEach((group) => {
            if (!elementIdsToRemove.includes(group.activeDropdownElementId)) {
                return;
            }

            const firstRemainingChild = [...group.children]
                .filter((child) => !elementIdsToRemove.includes(child.id))
                .sort((a, b) => b.renderOrder - a.renderOrder)[0];

            if (firstRemainingChild) {
                singleSelectGroupUpdates.push({ groupId: group.id, newActiveChildId: firstRemainingChild.id });
            }
        });

        creativeModel.removeElements(elementIdsToRemove);

        if (masksToUpdate) {
            masksToUpdate.forEach((mask) => {
                creativeModel.updateElement(mask.id, {
                    mask: null,
                });
            });
        }

        if (maskedElementsToUpdate) {
            maskedElementsToUpdate.forEach((element) => {
                if (elementIdsToRemove.includes(element.mask.elementId)) {
                    creativeModel.updateElement(element.id, {
                        mask: null,
                    });
                }
            });
        }

        // Auto-show the next child in Single Select groups whose active child was deleted
        singleSelectGroupUpdates.forEach(({ groupId, newActiveChildId }) => {
            creativeModel.updateElement(groupId, { activeDropdownElementId: newActiveChildId });
            creativeModel.updateElement(newActiveChildId, { hidden: false });
        });
    }, [isActiveElements, selectedElements, creativeModel, manipulationRenderer]);

    const handleDuplicate = useCallback(() => {
        if (!isActiveElements) {
            return;
        }

        const duplicatedElements = collectTopLevelElements(selectedElements).map((element) => {
            const duplicatedElement = element.getCopy();

            duplicatedElement.parent = element.parent;

            return duplicatedElement;
        });

        creativeModel.addElements(duplicatedElements);
    }, [isActiveElements, selectedElements, creativeModel, manipulationRenderer]);

    const handleCreateGroup = useCallback(() => {
        if (!isActiveElements) {
            return;
        }

        if (selectedElements.some((element) => element instanceof AudioElement)) {
            return;
        }

        const groupElement = creativeModel.wrapElementsIntoGroup(selectedElements.map((el) => el.id));
        manipulationRenderer.selectElement(groupElement.id);
    }, [isActiveElements, selectedElements, creativeModel, manipulationRenderer]);

    const handleUnGroup = useCallback(() => {
        if (!isActiveGroups) {
            return;
        }

        const elementToSelect = selectedGroups[selectedGroups.length - 1].children.reduce(
            (elA, elB) => ((elA?.renderOrder || 0) > (elB?.renderOrder || 0) ? elA : elB),
            null,
        );

        const editorElements = creativeModel.getAllElementsRecursively<VisualElement>('visual');
        const maskedElements = editorElements.filter((el) => el.mask && el.mask.elementId);

        selectedGroups.forEach((group) => {
            creativeModel.unwrapGroupElement(group.id);

            if (maskedElements) {
                maskedElements.forEach((element) => {
                    if (group.id === element.mask.elementId) {
                        creativeModel.updateElement(element.id, {
                            mask: null,
                        });
                    }
                });
            }

            if (group.mask && group.mask.elementId) {
                const sameElementMaskedElements = maskedElements
                    .filter((el) => el.id !== group.id)
                    .filter((el) => el.mask.elementId === group.mask.elementId);

                if (!sameElementMaskedElements.length) {
                    creativeModel.updateElement(group.mask.elementId, {
                        mask: null,
                    });
                }
            }
        });

        if (elementToSelect) {
            manipulationRenderer.selectElement(elementToSelect.id);
        } else if (setSelectedGroups) {
            setSelectedGroups([]);
        }
    }, [isActiveGroups, selectedGroups, creativeModel, manipulationRenderer]);

    const elementActions = useMemo(
        () => ({
            handleLock,
            handleDelete,
            handleSelection,
            handleDuplicate,
            handleCreateGroup,
            handleUnGroup,
        }),
        [creativeModel, forceUpdate, handleLock],
    );

    return elementActions;
};

export default useElementGeneralActions;
